import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TERMS_TITLE, TERMS_UPDATED, TERMS_UPDATED_LABEL, TERMS_SECTIONS, termsReady } from "../lib/termsOfUse";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * TermsOfUse -- issue #1340 (D367.3), `/terms`.
 *
 * ONE new-look page for both looks, with no `newUi` flag (the
 * `/account-deleted` precedent, D217: the old look gets no page of its own).
 * Structure copied from `PrivacyNoticeNew.jsx`: chromeless header with
 * `BrandMark`, a 480px column, the same two style objects, the same Back.
 *
 * HIDDEN UNTIL FILLED. While `termsReady()` is false the component returns
 * `<Navigate to="/" replace />`: the live site must never show a
 * placeholder. Every word is read from `lib/termsOfUse.js`; none is
 * written here.
 */
const sectionHeadingStyle = {
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 800,
  fontSize: 18,
  color: "var(--nw-ink)",
};

const bodyTextStyle = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.55,
  color: "var(--nw-ink-lede)",
};

/** Where Back goes: same rule as `/privacy` (#1125). */
const BACK_EXTRA_PATHS = ["/register"];
function backTarget(location) {
  const fromState = location?.state?.from;
  const fromQuery = new URLSearchParams(location?.search || "").get("from");
  return safeRedirectTarget(fromState ?? fromQuery, "/", { extraPaths: BACK_EXTRA_PATHS });
}
const BACK_LABEL_SETTINGS = "Back to Settings";
const BACK_LABEL_SIGN_UP = "Back to sign up";
const BACK_LABEL_HOME = "Back to CricketCompass";
const BRAND_WORD = "Compass";
function backLabel(target) {
  if (/^\/settings([/?#]|$)/.test(target)) return BACK_LABEL_SETTINGS;
  if (/^\/register([?#]|$)/.test(target)) return BACK_LABEL_SIGN_UP;
  return BACK_LABEL_HOME;
}

export default function TermsOfUse() {
  const location = useLocation();
  if (!termsReady()) return <Navigate to="/" replace />;
  const target = backTarget(location);
  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      <div
        data-testid="terms-page"
        style={{
          maxWidth: 480,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          padding: "24px 20px",
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            {BRAND_WORD}
          </span>
        </div>

        <h1 className="nw-title" style={{ margin: 0 }}>
          {TERMS_TITLE}
        </h1>

        <p data-testid="terms-updated" style={bodyTextStyle}>
          {TERMS_UPDATED_LABEL} {TERMS_UPDATED}
        </p>

        {TERMS_SECTIONS.map((section) => (
          <section
            key={section.id}
            id={section.id}
            data-testid={`terms-section-${section.id}`}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <h2 style={sectionHeadingStyle}>{section.heading}</h2>
            <p style={bodyTextStyle}>{section.body}</p>
          </section>
        ))}

        <Link
          to={target}
          data-testid="terms-back"
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            minHeight: 44, // G4: a 44px tap target
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--nw-navy)",
            textDecoration: "none",
          }}
        >
          <ArrowLeft size={14} /> {backLabel(target)}
        </Link>
      </div>
    </div>
  );
}
